import {
  Box,
  Button,
  Checkbox,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import {
  AlignLeft,
  Bookmark,
  BookmarkSimple,
  Browsers,
  Cards,
  CheckSquareOffset,
  CirclesThreePlus,
  Clock,
  Cube,
  FilmSlate,
  FilmStrip,
  Folders,
  FolderSimpleUser,
  FolderStar,
  Hexagon,
  Rectangle,
  Stack,
  Tag,
  Triangle,
  UserFocus,
  UserList,
  X,
} from "phosphor-react";
import React, { useEffect, useMemo, useState } from "react";
import { EpisodesIconCustom } from "../../../Utilities/Svg/EpisodesIcon";
import { ProjectMediaIconCustom } from "../../../Utilities/Svg/ProjectMediaIcon";
import { ReviewerIcon } from "../../../Utilities/Svg/Reviewer";
import useStyles from "./NewPageGroupModal.style";

const fieldStyle = {
  backgroundColor: "#252A38",
  borderRadius: "8px",
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& .MuiInputBase-input": { color: "#F9F9F9", padding: "10px 14px" },
};

const checkboxSx = {
  padding: "4px",
  color: "#606479",
  "&.Mui-checked": {
    color: "#6C65D9",
  },
};

const PAGE_ICONS = {
  updates: Clock,
  tasks: CheckSquareOffset,
  assets: Cube,
  episodes: EpisodesIconCustom,
  sequences: FilmStrip,
  shots: FilmSlate,
  reviews: ReviewerIcon,
  custom_pages: Cards,
  project_media: ProjectMediaIconCustom,
  playlist: FolderStar,
};

const GROUP_ICON_OPTIONS = [
  { id: "circles-three-plus", icon: CirclesThreePlus },
  { id: "folders", icon: Folders },
  { id: "stack", icon: Stack },
  { id: "align-left", icon: AlignLeft },
  { id: "user-list", icon: UserList },
  { id: "user-focus", icon: UserFocus },
  { id: "folder-simple-user", icon: FolderSimpleUser },
  { id: "browsers", icon: Browsers },
  { id: "bookmark-simple", icon: BookmarkSimple },
  { id: "bookmark", icon: Bookmark },
  { id: "tag", icon: Tag },
  { id: "triangle", icon: Triangle },
  { id: "rectangle", icon: Rectangle },
  { id: "hexagon", icon: Hexagon },
];

const PageIcon = ({ icon: Icon, size = 16 }) => {
  if (!Icon) {
    return (
      <span
        style={{
          width: size,
          height: size,
          borderRadius: "3px",
          border: "1px solid #606479",
          display: "inline-block",
          flexShrink: 0,
        }}
      />
    );
  }

  return <Icon size={size} color="#B1B5C9" />;
};

const PageSelectionRow = ({ page, selected, onToggle, classes }) => (
  <div
    className={`${classes.pageRow} ${
      selected ? classes.pageRowSelected : classes.pageRowUnselected
    }`}
    onClick={() => onToggle(page.key)}
    role="button"
    tabIndex={0}
    onKeyDown={(event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onToggle(page.key);
      }
    }}
  >
    <div className={classes.pageRowContent}>
      {page.type === "customPage" ? (
        <Cards size={18} color="#B1B5C9" />
      ) : (
        <PageIcon icon={PAGE_ICONS[page.key]} />
      )}
      <Typography className={classes.pageLabel}>{page.label}</Typography>
    </div>
    <Checkbox
      checked={selected}
      size="small"
      sx={checkboxSx}
      tabIndex={-1}
      onClick={(event) => event.stopPropagation()}
      onChange={() => onToggle(page.key)}
    />
  </div>
);

const NewPageGroupModal = ({
  handleClose,
  setSnackValue,
  editMode,
  initialData,
  onSubmit,
  pages,
  customPages,
}) => {
  const classes = useStyles();
  const [name, setName] = useState("");
  const [selectedIconId, setSelectedIconId] = useState("circles-three-plus");
  const [selectedPageIds, setSelectedPageIds] = useState([]);

  useEffect(() => {
    if (editMode && initialData) {
      setName(initialData.label || "");
      setSelectedIconId(initialData.iconId || "circles-three-plus");
      setSelectedPageIds(initialData.pageIds || []);
      return;
    }

    setName("");
    setSelectedIconId("circles-three-plus");
    setSelectedPageIds([]);
  }, [editMode, initialData]);

  const isSubmitDisabled = !name.trim() || selectedPageIds.length === 0;

  const handlePageToggle = (pageId) => {
    setSelectedPageIds((prev) =>
      prev.includes(pageId)
        ? prev.filter((id) => id !== pageId)
        : [...prev, pageId],
    );
  };

  const handleSubmit = () => {
    onSubmit({
      name: name.trim(),
      iconId: selectedIconId,
      pageIds: selectedPageIds,
    });
    handleClose();
  };

  return (
    <Box className={classes.modalContent}>
      <Box className={classes.header}>
        <Box>
          <Typography className={classes.title}>
            {editMode ? "Edit Page Group" : "New Page Group"}
          </Typography>
          <Typography className={classes.subtitle}>
            Group related pages under one button to keep the nav bar tidy
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ color: "#B1B5C9" }}
        >
          <X size={28} />
        </IconButton>
      </Box>

      <Box className={classes.scrollableBody}>
        <Box className={classes.section}>
          <Typography className={classes.label}>
            Name<span style={{ color: "#E41F1F" }}>*</span>
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g. Trackable Pages"
            value={name}
            onChange={(event) => setName(event.target.value)}
            variant="outlined"
            size="small"
            sx={fieldStyle}
            InputProps={{
              sx: {
                backgroundColor: "#252A38",
                borderRadius: "10px",
                "& fieldset": { border: "none" },
                color: "#F9F9F9",
              },
            }}
            inputProps={{ style: { color: "#F9F9F9" } }}
          />
        </Box>

        <Box className={classes.section}>
          <Typography className={classes.label}>Icons</Typography>
          <Box className={classes.iconGrid}>
            {GROUP_ICON_OPTIONS.map((option) => {
              const Icon = option.icon;
              const isSelected = selectedIconId === option.id;

              return (
                <button
                  key={option.id}
                  type="button"
                  className={`${classes.iconButton} ${
                    isSelected ? classes.iconButtonSelected : ""
                  }`}
                  onClick={() => setSelectedIconId(option.id)}
                >
                  <Icon size={18} color={isSelected ? "#6C65D9" : "#B1B5C9"} />
                </button>
              );
            })}
          </Box>
        </Box>

        <Box className={classes.section}>
          <Typography className={classes.label}>Pages in this group</Typography>
          <Box className={classes.pagesGrid}>
            {pages.map((page) => (
              <PageSelectionRow
                key={page.key}
                page={page}
                selected={selectedPageIds.includes(page.key)}
                onToggle={handlePageToggle}
                classes={classes}
              />
            ))}
            {customPages.map((page) => (
              <PageSelectionRow
                key={page.key}
                page={page}
                selected={selectedPageIds.includes(page.key)}
                onToggle={handlePageToggle}
                classes={classes}
              />
            ))}
          </Box>
        </Box>
      </Box>

      <Button
        fullWidth
        variant="contained"
        onClick={handleSubmit}
        disabled={isSubmitDisabled}
        className={classes.footer}
        sx={{
          color: "white",
          borderRadius: "10px",
          textTransform: "none",
          fontSize: "14px",
          padding: "10px 22px",
          bgcolor: isSubmitDisabled ? "#252A38" : "#6C65D9",
          "&:hover": {
            bgcolor: isSubmitDisabled ? "#252A38" : "#5a54c7",
          },
          "&.Mui-disabled": { color: "#606479" },
        }}
      >
        {editMode ? "Save group" : "Create group"}
      </Button>
    </Box>
  );
};

export default NewPageGroupModal;
